import {
  CSRF_HEADER,
  type DirectorySourceView,
  type Entity,
  type IssuedSourceToken,
  type UnmappedEntry,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, TriangleAlert, UserRoundX } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, ok } from "../api-errors.ts";
import { errorCopy } from "../error-copy.ts";
import { formatDateTime } from "../i18n/format.ts";
import { t } from "../i18n/i18n.ts";
import { shortId } from "../projects/readers.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import {
  GoogleSourceActions,
  GoogleSourceDetails,
  GoogleSourceSetup,
  isGoogleSource,
} from "./GoogleDirectorySource.tsx";
import { useSignInData } from "./SignInAdmin.tsx";

const keys = {
  sources: ["admin", "directory", "sources"] as const,
  unmapped: ["admin", "directory", "unmapped"] as const,
  entities: ["admin", "entities"] as const,
};

/** How many failures each source row shows before "and N more". */
const ERRORS_SHOWN = 3;

function formatTime(iso: string | null): string {
  return iso ? formatDateTime(iso) : t("directory.never");
}

const KINDS = ["idp", "scim", "push", "pull"] as const;
const kindLabel = (kind: string) =>
  KINDS.includes(kind as (typeof KINDS)[number])
    ? t(`directory.kindLabel.${kind as (typeof KINDS)[number]}`)
    : kind;
const statusLabel = (status: string) =>
  status === "active" || status === "disabled" ? t(`directory.statusLabel.${status}`) : status;
function errorLine(code: string, item: string | null): string {
  const reason = errorCopy(code) ?? t("directory.unknownReason");
  return item ? t("directory.errorLine", { reason, item }) : reason;
}

interface IdentityProviderOption {
  id: string;
  displayName: string;
}

/** The tenant's identity providers, for the source picker (support grants may read them). */
function useIdentityProviders(): { data: IdentityProviderOption[] | undefined } {
  const signin = useSignInData();
  return {
    data: signin.data?.identityProviders.map((p) => ({ id: p.id, displayName: p.displayName })),
  };
}

/**
 * Directory backoffice: the sources keeping groups and attributes in line with the customer's
 * directory (with their last sync, full reconciliation, staleness and failures), new SCIM and
 * push sources with their token shown once, and the unmapped org-code queue.
 */
export function DirectoryAdmin() {
  const { admin, readOnly } = useAdminApi();
  useTranslation();
  const sources = useQuery({
    queryKey: keys.sources,
    queryFn: async (): Promise<DirectorySourceView[]> => ok(await admin.directory.sources.$get()),
    retry: false,
  });
  const [issued, setIssued] = useState<IssuedSourceToken | null>(null);
  const [outcome, setOutcome] = useState<{
    tone: "success" | "warning" | "danger";
    text: string;
  } | null>(null);
  const forbidden = sources.error instanceof ApiError && sources.error.status === 403;

  return (
    <div data-esi-content="people" className="flex flex-col gap-8">
      <PageHeader title={t("directory.title")} lead={t("directory.intro")} />
      {forbidden ? (
        <Notice>{t("directory.adminsOnly")}</Notice>
      ) : (
        <>
          <section aria-labelledby="sources-heading" className="flex flex-col gap-3">
            <h2 id="sources-heading" className="text-sm font-semibold text-fg">
              {t("directory.sources")}
            </h2>
            {issued ? <IssuedToken issued={issued} onDismiss={() => setIssued(null)} /> : null}
            {outcome ? (
              <Notice tone={outcome.tone} live>
                {outcome.text}
              </Notice>
            ) : null}
            {sources.isError ? (
              <Notice tone="danger">{sources.error.message}</Notice>
            ) : sources.data ? (
              <SourcesTable sources={sources.data} onIssued={setIssued} onOutcome={setOutcome} />
            ) : (
              <Skeleton className="h-24 w-full" />
            )}
            {readOnly ? null : (
              <>
                <NewSource onIssued={setIssued} />
                <GoogleSourceSetup onAdded={(text) => setOutcome({ tone: "success", text })} />
              </>
            )}
          </section>
          <UnmappedQueue />
        </>
      )}
    </div>
  );
}

type SourceOutcome = { tone: "success" | "warning" | "danger"; text: string };

/**
 * Sources with their sync state; SCIM and push tokens can be rotated (shown once). From a wide
 * container a table, below it one stacked row per source with every figure of the table.
 */
export function SourcesTable(props: {
  sources: readonly DirectorySourceView[];
  onIssued: (issued: IssuedSourceToken) => void;
  /** A Google source's test or key outcome, shown above the table. */
  onOutcome?: (outcome: SourceOutcome) => void;
}) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const providers = useIdentityProviders();
  const [bindError, setBindError] = useState<string | null>(null);
  const rotate = useMutation({
    mutationFn: async (sourceId: string) =>
      ok(
        await admin.directory.sources[":sourceId"].token.$post(
          { param: { sourceId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (issued) => {
      props.onIssued(issued);
      void queryClient.invalidateQueries({ queryKey: keys.sources });
    },
  });

  if (props.sources.length === 0) {
    return <p className="text-sm text-fg-secondary">{t("directory.noSources")}</p>;
  }
  const cells: SourceCells = {
    providers: providers.data ?? null,
    onBindError: setBindError,
    rotate: {
      pending: rotate.isPending,
      sourceId: rotate.variables,
      mutate: (id) => rotate.mutate(id),
    },
    onOutcome: (o) => props.onOutcome?.(o),
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="@container">
        <SourceRows sources={props.sources} cells={cells} />
        <SourceTable sources={props.sources} cells={cells} />
      </div>
      {rotate.isError ? <InlineError>{rotate.error.message}</InlineError> : null}
      {bindError ? <InlineError>{bindError}</InlineError> : null}
    </div>
  );
}

/** What both renderings of the sources share: the providers, the rotation and the outcomes. */
interface SourceCells {
  providers: readonly IdentityProviderOption[] | null;
  onBindError: (message: string | null) => void;
  rotate: { pending: boolean; sourceId: string | undefined; mutate: (sourceId: string) => void };
  onOutcome: (outcome: SourceOutcome) => void;
}

/** From a wide container: one row per source, seven columns (a Google source adds a line). */
function SourceTable({
  sources,
  cells,
}: {
  sources: readonly DirectorySourceView[];
  cells: SourceCells;
}) {
  return (
    <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface-raised @3xl:block">
      <table className="w-full min-w-[60rem] text-left text-sm">
        <caption className="sr-only">{t("directory.caption")}</caption>
        <thead className="border-b border-line text-xs text-fg-muted">
          <tr>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("directory.name")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("directory.identityProvider")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("directory.status")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("directory.lastSync")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("directory.lastFull")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("directory.errors")}
            </th>
            <th scope="col" className="px-4 py-2">
              <span className="sr-only">{t("directory.rotate")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {sources.map((s) => [
            <tr key={s.id} className={isGoogleSource(s) ? "border-b-0 align-top" : "align-top"}>
              <td className="px-4 py-2.5">
                <span className="block font-medium text-fg">{s.name}</span>
                <span className="text-xs text-fg-muted">{kindLabel(s.kind)}</span>
              </td>
              <td className="px-4 py-2.5">
                <SourceIdentityProvider
                  source={s}
                  providers={cells.providers}
                  onError={cells.onBindError}
                />
              </td>
              <td className="px-4 py-2.5 text-fg-secondary">
                <SourceStatus source={s} />
              </td>
              <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                {formatTime(s.lastSyncAt)}
              </td>
              <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                {formatTime(s.lastFullAt)}
              </td>
              <td className="px-4 py-2.5">
                <SourceErrors source={s} />
              </td>
              <td className="px-4 py-2 text-right whitespace-nowrap">
                <SourceActions source={s} cells={cells} />
              </td>
            </tr>,
            // The account, scope and skipped counts of a Google source, across the table.
            isGoogleSource(s) ? (
              <tr key={`${s.id}-google`} className="!border-t-0">
                <td colSpan={7} className="px-4 pt-0 pb-2.5">
                  <GoogleSourceDetails source={s} />
                </td>
              </tr>
            ) : null,
          ])}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Below a wide container, the sources as rows: the name and its kind with the status, then the
 * identity provider, both syncs and the failures, worded; a Google source's account and scope;
 * the actions last. Nothing is truncated or scrolled: every figure of the table is here.
 */
function SourceRows({
  sources,
  cells,
}: {
  sources: readonly DirectorySourceView[];
  cells: SourceCells;
}) {
  return (
    <ul
      aria-label={t("directory.caption")}
      className="divide-y divide-line rounded-lg border border-line bg-surface-raised @3xl:hidden"
    >
      {sources.map((s) => (
        <li key={s.id} className="flex flex-col gap-2.5 px-4 py-3 text-sm">
          <span className="flex flex-col">
            <span className="font-medium text-fg">{s.name}</span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-fg-secondary">
              <span>{kindLabel(s.kind)}</span>
              <span aria-hidden="true">·</span>
              <SourceState source={s} />
            </span>
            <SourceLapsed source={s} />
          </span>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs @sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-0.5 @sm:col-span-2">
              <dt className="text-fg-muted">{t("directory.identityProvider")}</dt>
              <dd className="text-sm">
                <SourceIdentityProvider
                  source={s}
                  providers={cells.providers}
                  onError={cells.onBindError}
                />
              </dd>
            </div>
            <div className="flex min-w-0 flex-col">
              <dt className="text-fg-muted">{t("directory.lastSync")}</dt>
              <dd className="text-fg-secondary tabular-nums">{formatTime(s.lastSyncAt)}</dd>
            </div>
            <div className="flex min-w-0 flex-col">
              <dt className="text-fg-muted">{t("directory.lastFull")}</dt>
              <dd className="text-fg-secondary tabular-nums">{formatTime(s.lastFullAt)}</dd>
            </div>
            <div className="flex min-w-0 flex-col @sm:col-span-2">
              <dt className="text-fg-muted">{t("directory.errors")}</dt>
              <dd>
                <SourceErrors source={s} />
              </dd>
            </div>
          </dl>
          {isGoogleSource(s) ? <GoogleSourceDetails source={s} /> : null}
          <SourceActions source={s} cells={cells} align="start" />
        </li>
      ))}
    </ul>
  );
}

/** The status, a stale badge and, for sign-in groups, how many people must sign in again. */
function SourceStatus({ source: s }: { source: DirectorySourceView }) {
  return (
    <>
      <SourceState source={s} />
      <SourceLapsed source={s} />
    </>
  );
}

/** The status and a stale badge. */
function SourceState({ source: s }: { source: DirectorySourceView }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {statusLabel(s.status)}
      {s.stale ? (
        <Badge
          variant="outline"
          className="gap-1 border-warning-border/60 bg-warning-bg text-warning-fg"
          title={t("directory.staleHint")}
        >
          <TriangleAlert aria-hidden="true" />
          {t("directory.stale")}
        </Badge>
      ) : null}
    </span>
  );
}

/** For sign-in groups, how many people must sign in again. */
function SourceLapsed({ source: s }: { source: DirectorySourceView }) {
  return s.kind === "idp" && typeof s.lapsedUsers === "number" && s.lapsedUsers > 0 ? (
    <span
      className="mt-0.5 flex items-center gap-1 text-xs text-fg-muted tabular-nums"
      title={t("directory.lapsedHint")}
    >
      <UserRoundX aria-hidden="true" className="size-3" strokeWidth={1.75} />
      {t("directory.lapsed", { count: s.lapsedUsers })}
    </span>
  ) : null;
}

/** The latest failures, then "and N more". */
function SourceErrors({ source: s }: { source: DirectorySourceView }) {
  if (s.errors.length === 0) {
    return <span className="text-fg-muted">{t("directory.noErrors")}</span>;
  }
  return (
    <ul className="flex flex-col gap-0.5 text-danger-fg">
      {s.errors.slice(0, ERRORS_SHOWN).map((e) => (
        <li key={`${e.at}-${e.reason}-${e.item ?? ""}`}>
          {errorLine(e.reason, e.item)}
          <span className="text-fg-muted tabular-nums"> · {formatTime(e.at)}</span>
        </li>
      ))}
      {s.errors.length > ERRORS_SHOWN ? (
        <li className="text-fg-muted">
          {t("directory.moreErrors", { count: s.errors.length - ERRORS_SHOWN })}
        </li>
      ) : null}
    </ul>
  );
}

/** Rotate a SCIM or push token; test, enable or re-key a Google source. */
function SourceActions(props: {
  source: DirectorySourceView;
  cells: SourceCells;
  align?: "start" | "end";
}) {
  const { readOnly } = useAdminApi();
  const { source: s, cells } = props;
  if (readOnly) {
    return null;
  }
  const rotate =
    s.kind === "scim" || s.kind === "push" ? (
      <Button
        variant="ghost"
        size="xs"
        disabled={cells.rotate.pending}
        onClick={() => cells.rotate.mutate(s.id)}
      >
        <KeyRound aria-hidden="true" />
        {cells.rotate.pending && cells.rotate.sourceId === s.id
          ? t("directory.rotating")
          : t("directory.rotate")}
      </Button>
    ) : null;
  const google = isGoogleSource(s) ? (
    <GoogleSourceActions source={s} onOutcome={cells.onOutcome} align={props.align} />
  ) : null;
  if (props.align !== "start") {
    return (
      <>
        {rotate}
        {google}
      </>
    );
  }
  // Ghost buttons line up with the text above through their own padding.
  return rotate || google ? (
    <div className="-mx-2 flex flex-wrap gap-1">
      {rotate}
      {google}
    </div>
  ) : null;
}

/**
 * Which identity provider a source speaks for. An `idp` source names its own; a SCIM, push or
 * pull source picks one so the subjects it records can bind a first sign-in, only through that
 * provider. With several providers, a source naming none is flagged: its subjects are dropped.
 */
function SourceIdentityProvider(props: {
  source: DirectorySourceView;
  providers: readonly IdentityProviderOption[] | null;
  onError: (message: string | null) => void;
}) {
  const { admin, readOnly } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const { source, providers } = props;
  const current = source.identityProviderId ?? null;
  const bind = useMutation({
    mutationFn: async (identityProviderId: string | null) =>
      ok(
        await admin.directory.sources[":sourceId"].$patch(
          { param: { sourceId: source.id }, json: { identityProviderId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onMutate: () => props.onError(null),
    onError: (error) => props.onError(error.message),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.sources }),
  });
  const name = (id: string | null) =>
    id === null
      ? t("directory.noIdentityProvider")
      : (providers?.find((p) => p.id === id)?.displayName ?? shortId(id));
  // With any provider at all, an unbound source drops every subject it sends.
  const unbound = source.kind !== "idp" && current === null && (providers?.length ?? 0) > 0;
  const flag = unbound ? (
    <span className="mt-1 flex items-start gap-1 text-xs text-warning-fg">
      <TriangleAlert aria-hidden="true" className="mt-px size-3 shrink-0" strokeWidth={1.75} />
      {t("directory.unboundHint")}
    </span>
  ) : null;
  if (source.kind === "idp" || readOnly || providers === null) {
    return (
      <>
        <span className="text-fg-secondary">{name(current)}</span>
        {flag}
      </>
    );
  }
  return (
    <>
      <NativeSelect
        aria-label={t("directory.identityProviderFor", { name: source.name })}
        value={current ?? ""}
        disabled={bind.isPending}
        onChange={(e) => bind.mutate(e.currentTarget.value || null)}
        className="h-8 w-48 text-[13px]"
      >
        <option value="">{t("directory.noIdentityProvider")}</option>
        {providers.map((p) => (
          <option key={p.id} value={p.id}>
            {p.displayName}
          </option>
        ))}
      </NativeSelect>
      {flag}
    </>
  );
}

/** A token, shown once, with the SCIM base URL when it is a SCIM source. */
export function IssuedToken(props: { issued: IssuedSourceToken; onDismiss: () => void }) {
  const id = useId();
  const [copied, setCopied] = useState(false);
  const { issued } = props;
  return (
    <div
      role="status"
      aria-labelledby={`${id}-title`}
      className="flex flex-col gap-3 rounded-lg border border-info-border/50 bg-info-bg px-4 py-3.5"
    >
      <div className="flex flex-col gap-0.5">
        <h3 id={`${id}-title`} className="text-sm font-semibold text-fg">
          {t("directory.tokenTitle", { name: issued.source.name })}
        </h3>
        <p className="text-sm text-fg-secondary">{t("directory.tokenOnce")}</p>
      </div>
      {issued.scimBaseUrl ? (
        <TokenField label={t("directory.scimUrl")} value={issued.scimBaseUrl} />
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        <TokenField label={t("directory.token")} value={issued.token} />
        <Button
          variant="outline"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(issued.token);
              setCopied(true);
            } catch {
              // No clipboard permission: the field stays selectable.
            }
          }}
        >
          <Copy aria-hidden="true" />
          {copied ? t("directory.copied") : t("directory.copy")}
        </Button>
      </div>
      <div>
        <Button size="sm" onClick={props.onDismiss}>
          {t("directory.dismiss")}
        </Button>
      </div>
    </div>
  );
}

function TokenField({ label, value }: { label: string; value: string }) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="bg-surface-raised font-mono"
      />
    </div>
  );
}

function NewSource({ onIssued }: { onIssued: (issued: IssuedSourceToken) => void }) {
  const { admin } = useAdminApi();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"scim" | "push">("scim");
  const providers = useIdentityProviders();
  // Undefined until the admin picks: a tenant with one provider defaults to it.
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const only = providers.data?.length === 1 ? (providers.data[0]?.id ?? null) : null;
  const identityProviderId = picked === undefined ? only : picked;
  const create = useMutation({
    mutationFn: async () =>
      ok(
        await admin.directory.sources.$post(
          { json: { kind, name: name.trim(), identityProviderId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (result) => {
      if (result.token !== null) {
        onIssued(result as IssuedSourceToken);
      }
      setName("");
      void queryClient.invalidateQueries({ queryKey: keys.sources });
    },
  });
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) {
          create.mutate();
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{t("directory.newName")}</Label>
        <Input
          id={`${id}-name`}
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.currentTarget.value)}
          className="w-64"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-kind`}>{t("directory.newKind")}</Label>
        <NativeSelect
          id={`${id}-kind`}
          value={kind}
          onChange={(e) => setKind(e.currentTarget.value === "push" ? "push" : "scim")}
          className="w-64"
        >
          <option value="scim">{t("directory.newKindLabel.scim")}</option>
          <option value="push">{t("directory.newKindLabel.push")}</option>
        </NativeSelect>
      </div>
      {providers.data && providers.data.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-idp`}>{t("directory.identityProvider")}</Label>
          <NativeSelect
            id={`${id}-idp`}
            value={identityProviderId ?? ""}
            onChange={(e) => setPicked(e.currentTarget.value || null)}
            className="w-64"
            aria-describedby={`${id}-idp-help`}
          >
            <option value="">{t("directory.noIdentityProvider")}</option>
            {providers.data.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </NativeSelect>
        </div>
      ) : null}
      <Button type="submit" variant="outline" disabled={!name.trim() || create.isPending}>
        {create.isPending ? t("directory.adding") : t("directory.add")}
      </Button>
      {providers.data && providers.data.length > 0 ? (
        <p id={`${id}-idp-help`} className="basis-full text-xs text-fg-muted">
          {t("directory.identityProviderHelp")}
        </p>
      ) : null}
      {create.isError ? (
        <div className="basis-full">
          <InlineError>{create.error.message}</InlineError>
        </div>
      ) : null}
    </form>
  );
}

/** People whose org code no mapping covers; mapping a code applies to all of them at once. */
function UnmappedQueue() {
  const { admin, readOnly } = useAdminApi();
  const queue = useQuery({
    queryKey: keys.unmapped,
    queryFn: async (): Promise<UnmappedEntry[]> => ok(await admin.directory.unmapped.$get()),
    retry: false,
  });
  const entities = useQuery({
    queryKey: keys.entities,
    queryFn: async (): Promise<Entity[]> => ok(await admin.entities.$get()),
    retry: false,
  });
  const [notice, setNotice] = useState<string | null>(null);
  const codes = [...new Set((queue.data ?? []).map((e) => e.code))];

  return (
    <section aria-labelledby="unmapped-heading" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id="unmapped-heading" className="text-sm font-semibold text-fg">
          {t("directory.unmapped")}
        </h2>
        <p className="max-w-prose text-sm text-fg-secondary">{t("directory.unmappedIntro")}</p>
      </div>
      {notice ? (
        <Notice tone="success" live>
          {notice}
        </Notice>
      ) : null}
      {queue.isError ? (
        <Notice tone="danger">{queue.error.message}</Notice>
      ) : !queue.data ? (
        <Skeleton className="h-16 w-full" />
      ) : queue.data.length === 0 ? (
        <p className="text-sm text-fg-secondary">{t("directory.noUnmapped")}</p>
      ) : (
        <>
          <div className="@container">
            <ul
              aria-label={t("directory.unmappedCaption")}
              className="divide-y divide-line rounded-lg border border-line bg-surface-raised @3xl:hidden"
            >
              {queue.data.map((e) => (
                <li key={`${e.userId}-${e.sourceId}`} className="flex flex-col gap-2 px-4 py-3">
                  <span className="text-sm text-fg">{e.displayName ?? shortId(e.userId)}</span>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs @sm:grid-cols-3">
                    <div className="flex min-w-0 flex-col">
                      <dt className="text-fg-muted">{t("directory.code")}</dt>
                      <dd className="font-mono break-all text-fg">{e.code}</dd>
                    </div>
                    <div className="flex min-w-0 flex-col">
                      <dt className="text-fg-muted">{t("directory.source")}</dt>
                      <dd className="text-fg-secondary tabular-nums">{shortId(e.sourceId)}</dd>
                    </div>
                    <div className="col-span-2 flex min-w-0 flex-col @sm:col-span-1">
                      <dt className="text-fg-muted">{t("directory.since")}</dt>
                      <dd className="text-fg-secondary tabular-nums">{formatTime(e.since)}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface-raised @3xl:block">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <caption className="sr-only">{t("directory.unmappedCaption")}</caption>
                <thead className="border-b border-line text-xs text-fg-muted">
                  <tr>
                    <th scope="col" className="px-4 py-2 font-medium">
                      {t("directory.person")}
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      {t("directory.code")}
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      {t("directory.source")}
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      {t("directory.since")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {queue.data.map((e) => (
                    <tr key={`${e.userId}-${e.sourceId}`}>
                      <td className="px-4 py-2.5 text-fg">{e.displayName ?? shortId(e.userId)}</td>
                      <td className="px-4 py-2.5 font-mono text-fg">{e.code}</td>
                      <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                        {shortId(e.sourceId)}
                      </td>
                      <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                        {formatTime(e.since)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {entities.data && entities.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {codes.map((code) => (
                <li key={code}>
                  {readOnly ? null : (
                    <MapCode code={code} entities={entities.data} onMapped={setNotice} />
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </section>
  );
}

function MapCode(props: {
  code: string;
  entities: readonly Entity[];
  onMapped: (notice: string) => void;
}) {
  const { admin } = useAdminApi();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [entityId, setEntityId] = useState(props.entities[0]?.id ?? "");
  const map = useMutation({
    mutationFn: async () =>
      ok(
        await admin.directory["org-codes"].$post(
          { json: { code: props.code, entityId, sourceId: null } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (result) => {
      props.onMapped(t("directory.mapped", { code: props.code, count: result.applied }));
      void queryClient.invalidateQueries({ queryKey: keys.unmapped });
    },
  });
  return (
    <form
      className="flex flex-wrap items-center gap-2 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        map.mutate();
      }}
    >
      <span className="font-mono text-fg">{props.code}</span>
      <label htmlFor={id} className="text-fg-secondary">
        {t("directory.mapTo")}
      </label>
      <NativeSelect
        id={id}
        aria-label={t("directory.chooseEntity", { code: props.code })}
        value={entityId}
        onChange={(e) => setEntityId(e.currentTarget.value)}
        className="w-56"
      >
        {props.entities.map((entity) => (
          <option key={entity.id} value={entity.id}>
            {entity.name}
          </option>
        ))}
      </NativeSelect>
      <Button type="submit" variant="outline" size="sm" disabled={!entityId || map.isPending}>
        {map.isPending ? t("directory.mapping") : t("directory.map")}
      </Button>
      {map.isError ? <InlineError>{map.error.message}</InlineError> : null}
    </form>
  );
}
